import React, { useCallback, useEffect, useState } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { getConnectedUsers, revokeUserSessions, errorMessage } from '../services/api';
import { agentLabel } from '../utils/kernelDevice';
import { fmtDateTime } from '../utils/kernelFormat';
import { userColor } from '../utils/userColor';
import './kernelConnectedUsers.css';

const REFRESH_MS = 30000;

const initials = (u) => {
  const letters = `${(u.name || '').trim()[0] || ''}${(u.surname || '').trim()[0] || ''}` || (u.email || '?').slice(0, 2);
  return letters.toUpperCase();
};

/**
 * «Utenti connessi» della gestione utenti: chi ha almeno un accesso attivo nell'azienda, con l'email usata per il login,
 * i dispositivi e l'ultima attività. Chi gestisce gli utenti può disconnettere un collega (tutti i suoi accessi).
 */
function KernelConnectedUsers() {
  const { t } = useLanguage();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    getConnectedUsers()
      .then((res) => {
        setUsers(res.users || []);
        setError('');
      })
      .catch((err) => setError(errorMessage(err)));
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const disconnect = async (u) => {
    if (!window.confirm(t('connectedUsers.confirm', { name: [u.name, u.surname].filter(Boolean).join(' ') || u.email }))) return;
    setBusyId(u.id);
    setError('');
    try {
      await revokeUserSessions(u.id);
      load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  if (users === null && !error) return null;
  const online = (users || []).filter((u) => u.online).length;

  return (
    <section className="card kernel-connected" aria-labelledby="kernel-connected-title">
      <div className="card-header">
        <h3 id="kernel-connected-title">{t('connectedUsers.title')}</h3>
        <span className="kernel-connected-count">{t('connectedUsers.count', { online, total: (users || []).length })}</span>
      </div>
      <div className="card-body">
        {error && <div className="error-message">{error}</div>}
        {users && users.length === 0 && <p className="kernel-connected-empty">{t('connectedUsers.none')}</p>}
        {users && users.length > 0 && (
          <ul className="kernel-connected-list">
            {users.map((u) => {
              const devices = [...new Set(u.sessions.map((s) => agentLabel(s.user_agent) || t('sessions.unknownDevice')))];
              return (
                <li key={u.id} className="kernel-connected-item">
                  <span className="kernel-connected-avatar" style={{ background: userColor(u) }} aria-hidden="true">{initials(u)}</span>
                  <span className="kernel-connected-text">
                    <span className="kernel-connected-name">
                      {[u.name, u.surname].filter(Boolean).join(' ') || u.email}
                      {u.is_you && <span className="kernel-session-badge">{t('connectedUsers.you')}</span>}
                    </span>
                    <span className="kernel-connected-email">{u.email}</span>
                    <span className="kernel-connected-meta">
                      {devices.slice(0, 2).join(', ')}{devices.length > 2 ? ` +${devices.length - 2}` : ''}
                      {u.sessions.length > 1 ? ` · ${t('connectedUsers.sessions', { count: u.sessions.length })}` : ''}
                    </span>
                  </span>
                  <span className="kernel-connected-status">
                    <span className={'kernel-connected-dot' + (u.online ? ' is-online' : '')} aria-hidden="true" />
                    <span>
                      <strong>{u.online ? t('connectedUsers.online') : t('connectedUsers.idle')}</strong>
                      <small>{fmtDateTime(u.last_seen_at)}</small>
                    </span>
                  </span>
                  {!u.is_you && (
                    <button type="button" className="btn btn-secondary btn-sm" disabled={busyId === u.id} onClick={() => disconnect(u)}>
                      {t('connectedUsers.disconnect')}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}

export default KernelConnectedUsers;
